import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { formatText } from 'bernie-l10n';
import { ViewLarge, Viewport, ViewSmall } from '@shared-ui/viewport-context';

import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLink } from 'uitk-react-link';
import { UitkRemovablePill } from 'uitk-react-pill';
import { UitkSpacing } from 'uitk-react-spacing';

import { UitkExternalButton } from 'uitk-react-button';
import { ActivityDestinationViewSearch } from '../../../common/__generated__/api';

import { LinkTracked } from '../../../utils/click-trackers';
import { useStores } from '../../../utils/hooks';

import { getPageId } from '../../../helpers/common-helper';
import { handlePillsClick } from '../../../helpers/activity-search-helper';

export interface FilterPillsProps {
  filters: ActivityDestinationViewSearch.AppliedFilters[];
  clearLabel?: string | null;
  clearAnalytics?: ActivityDestinationViewSearch.ClientSideAnalytics;
  clearURL?: string;
}

export const FilterPills: React.FC<FilterPillsProps> = ({
  filters,
  clearLabel,
  clearAnalytics,
  clearURL
}): JSX.Element | null => {
  const { activitySearchStore, analytics } = useStores();
  const history = useHistory();
  const location = useLocation();
  const pageId = getPageId(activitySearchStore);

  if (!filters?.length) {
    return null;
  }

  const Pills: React.FC = (): JSX.Element => (
    <>
      {filters.map((filter: ActivityDestinationViewSearch.AppliedFilters, index: number) => (
        <UitkLayoutFlexItem key={`pill-${index}`}>
          <div data-testid="filter-pill">
            <UitkRemovablePill
              id={`pill-${index}`}
              disabled={false}
              onClick={(): void =>
                handlePillsClick(history, location, filter?.nextSearchCriteria?.selections || [], pageId, analytics)
              }
              altText={
                filter.pill.accessibility || formatText('lx.pills.remove.accessibility.text', filter.pill.primary)
              }
            >
              <span aria-hidden="true">{filter.pill.primary}</span>
            </UitkRemovablePill>
          </div>
        </UitkLayoutFlexItem>
      ))}
    </>
  );

  const ClearAllPill: React.FC = (): JSX.Element =>
    clearLabel ? (
      <UitkLayoutFlexItem>
        <UitkSpacing padding={{ blockstart: 'one' }}>
          <UitkExternalButton>
            <div data-testid="filter-remove-pill">
              <UitkLink inline noWrap>
                <LinkTracked
                  rfrr={clearAnalytics?.referrerId || ''}
                  linkName={clearAnalytics?.linkName || ''}
                  to={clearURL}
                >
                  {clearLabel}
                </LinkTracked>
              </UitkLink>
            </div>
          </UitkExternalButton>
        </UitkSpacing>
      </UitkLayoutFlexItem>
    ) : (
      <></>
    );

  return (
    <Viewport>
      <ViewSmall>
        <UitkSpacing margin={{ block: 'three' }}>
          <UitkLayoutFlex className="uitk-scroll-horizontal" space="two">
            <Pills />
            <ClearAllPill />
          </UitkLayoutFlex>
        </UitkSpacing>
      </ViewSmall>
      <ViewLarge>
        <UitkSpacing margin={{ block: 'three' }}>
          <UitkLayoutFlex space="two" wrap="wrap">
            <Pills />
            <ClearAllPill />
          </UitkLayoutFlex>
        </UitkSpacing>
      </ViewLarge>
    </Viewport>
  );
};
